/**
 * 统一日志工具
 * - 开发环境：输出所有日志
 * - 生产环境：只输出错误日志，过滤敏感信息
 */

const isDevelopment = process.env.NODE_ENV === 'development';
const isDebugMode = process.env.REACT_APP_DEBUG === 'true';
// Verbose mode: set to true to see all logs in development
// By default, only errors are shown to keep console clean
const isVerboseMode = process.env.REACT_APP_VERBOSE === 'true' || isDebugMode;

// 敏感信息关键词（用于过滤日志）
const SENSITIVE_KEYS = [
  'apiKey',
  'api_key',
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'token',
  'password',
  'secret',
  'credential'
];

/**
 * 过滤敏感信息
 * @param data 要过滤的数据
 * @returns 过滤后的数据
 */
function sanitizeData(data: any): any {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }

  const sanitized: any = {};
  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();
    const isSensitive = SENSITIVE_KEYS.some(sk => lowerKey.includes(sk.toLowerCase()));
    
    if (isSensitive) {
      // 保留布尔值和数字（如果需要），但隐藏字符串值
      if (typeof value === 'boolean' || typeof value === 'number') {
        sanitized[key] = value;
      } else if (value) {
        sanitized[key] = '***REDACTED***';
      } else {
        sanitized[key] = value;
      }
    } else {
      sanitized[key] = sanitizeData(value);
    }
  }
  return sanitized;
}

/**
 * 统一日志工具
 * 
 * @example
 * logger.log('User action:', data);
 * logger.error('Error occurred:', error);
 * logger.secure('Credentials:', { apiKey: 'xxx' }); // 会自动脱敏
 */
export const logger = {
  /**
   * 普通日志（需要开启 verbose 模式）
   * 用于常规调试信息
   * 设置 REACT_APP_VERBOSE=true 或 REACT_APP_DEBUG=true 开启
   */
  log: (...args: any[]) => {
    if (isVerboseMode) {
      console.log(...args);
    }
  },

  /**
   * 错误日志（始终记录，但生产环境会脱敏）
   * 用于错误信息，生产环境也会记录
   */
  error: (...args: any[]) => {
    // 生产环境过滤敏感信息
    const sanitized = isDevelopment 
      ? args 
      : args.map(arg => typeof arg === 'object' ? sanitizeData(arg) : arg);
    console.error(...sanitized);
  },

  /**
   * 警告日志（需要开启 verbose 模式）
   * 用于警告信息
   * 设置 REACT_APP_VERBOSE=true 或 REACT_APP_DEBUG=true 开启
   */
  warn: (...args: any[]) => {
    if (isVerboseMode) {
      console.warn(...args);
    }
  },

  /**
   * 调试日志（仅开发环境 + 调试模式）
   * 用于详细的调试信息，需要明确开启调试模式
   */
  debug: (...args: any[]) => {
    if (isDebugMode) {
      console.log('🔍 [DEBUG]', ...args);
    }
  },

  /**
   * 安全日志（记录敏感信息，但会脱敏）
   * 用于需要记录但不暴露敏感信息的场景
   * 例如：记录认证状态，但不暴露 token
   */
  secure: (...args: any[]) => {
    const sanitized = args.map(arg => typeof arg === 'object' ? sanitizeData(arg) : arg);
    if (isDevelopment) {
      console.log('🔐 [SECURE]', ...sanitized);
    }
    // 生产环境不输出，或输出脱敏后的信息
    if (process.env.REACT_APP_LOG_SECURE === 'true') {
      console.log('🔐 [SECURE]', ...sanitized);
    }
  }
};

// 导出默认实例
export default logger;

